import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy
} from "@angular/core";
import { Subject } from "rxjs";
import {
  State,
  SortDescriptor,
  CompositeFilterDescriptor,
  process
} from "@progress/kendo-data-query";
import {
  FilterService,
  DataStateChangeEvent
} from "@progress/kendo-angular-grid";
import { takeUntil } from "rxjs/operators";
import { GridDisplayType } from "../../grid-display-type";
import { KendoGridEntity } from "./read-only-grid-definition";
import { GridDataConverterService } from "../../grid-data-converter.service";
import { GridColumnMode } from "../../grid-column-mode";
import { kendoPropertySplitter } from "src/app/shared/shared.constants";
import { WebApiService } from 'src/app/shared/web-api/web-api.service';
import { PicklistService } from 'src/app/shared/picklist/picklist.service';
import { GridService } from '../../grid.service';

@Component({
  standalone: false,
  selector: "app-read-only-grid",
  templateUrl: "./read-only-grid.component.html",
  styleUrls: ["./read-only-grid.component.scss"]
})
export class ReadOnlyGridComponent implements OnInit, OnDestroy {
  //#region properties

  //#region Inputs and outputs
  @Input() gridName: string;

  @Input() gridDefinition: KendoGridEntity;

  @Input() refresh$: Subject<void>;

  @Output() CurrentSelected: EventEmitter<any> = new EventEmitter();
  //#endregion

  last: string[] = [];

  public picklists: { [id: string]: [string] }[] = [];

  public currentSelected: string[] = [];

  public dummyState: State = {
    take: 50,
    skip: 0
  };

  GridDisplayType = GridDisplayType;

  aFilterService: FilterService = new FilterService();

  private _onDestroy$ = new Subject<void>();

  public get pageInfo(): string {
    return `Page ${this.gridDefinition.state.skip /
      this.gridDefinition.state.take} of`;
  }

  //#endregion

  constructor(
    private kendoConverter: GridDataConverterService,
    private picklistService: PicklistService,
    private gridService: GridService
  ) {}

  ngOnInit() {
    this.gridDefinition.isLoading = true;
    if (this.refresh$) {
      this.refresh$.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
        this.dataStateChange();
      });
    }

    if (!this.gridDefinition) {
      this.gridDefinition = new KendoGridEntity();
      this.gridDefinition.gridName = this.gridName;
    }

    if (this.gridDefinition.columns) {
      this.dataStateChange();
      if (this.gridDefinition.project) {
        this.getProjectPicklistColumns();
      } else {
        this.getSystemPicklistColumns();
      }
    } else if (this.gridDefinition.project && this.gridDefinition.group) {
      this.getProjectGrid();
    } else {
      if (this.gridDefinition.gridName) {
        this.getSystemGrid();
      }
    }
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public getProjectPicklistColumns() {
    for (let index = 0; index < this.gridDefinition.columns.length; index++) {
      const element = this.gridDefinition.columns[index];
      if (
        element.GridDisplayType.toString() ===
        GridDisplayType[GridDisplayType.PickList]
      ) {
        this.picklistService
          .getPicklistValuesForProject(
            element.PicklistGroupId,
            this.gridDefinition.project
          )
          .pipe(takeUntil(this._onDestroy$))
          .subscribe(x => {
            this.picklists[element.PropertyName] = x;
          });
      }
    }
  }

  public getSystemPicklistColumns() {
    for (let index = 0; index < this.gridDefinition.columns.length; index++) {
      const element = this.gridDefinition.columns[index];
      if (
        element.GridDisplayType.toString() ===
        GridDisplayType[GridDisplayType.PickList]
      ) {
        this.picklistService
          .getPicklistValues(element.PicklistGroupId)
          .pipe(takeUntil(this._onDestroy$))
          .subscribe(x => {
            this.picklists[element.PropertyName] = x;
          });
      }
    }
  }

  public getProjectGrid() {
    this.gridService
      .getGridDefinition(this.gridDefinition.project, this.gridDefinition.group)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(grid => {
        this.gridDefinition.gridName = grid.Name;
        this.gridDefinition.gridId = grid.Id;
        this.gridDefinition.gridEntityName = grid.EntityName;
        this.gridDefinition.columns = grid.Columns;
        if (this.gridDefinition.includeHasDocuments) {
          const docColumn: any = {
            Availibility: GridColumnMode.ViewAndEdit,
            DefaultValue: "",
            EntityName: "Local",
            GridDisplayType: "Document",
            Header: "",
            Id: null,
            Index: -1,
            IsFilterable: false,
            IsGroupable: false,
            IsMainEntityColumn: false,
            IsPicklistColumn: false,
            IsReadOnly: true,
            IsReferenceField: false,
            IsSortable: false,
            PicklistGroupId: null,
            PropertyName: "Documents",
            Visibility: GridColumnMode.ViewAndEdit,
            Width: 40
          };
          this.gridDefinition.columns.push(docColumn);
        }
        this.gridDefinition.columns = this.kendoConverter.sortAndRenameColumns(
          this.gridDefinition.columns
        );
        this.dataStateChange();

        this.getProjectPicklistColumns();
      });
  }

  public getSystemGrid() {
    this.gridService
      .postSystemsDetailsGrid(this.gridDefinition.gridName)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(grid => {
        this.gridDefinition.gridName = grid.Name;
        this.gridDefinition.gridId = grid.Id;
        this.gridDefinition.gridEntityName = grid.EntityName;
        this.gridDefinition.columns = grid.Columns;
        if (this.gridDefinition.includeHasDocuments) {
          const docColumn: any = {
            Availibility: GridColumnMode.ViewAndEdit,
            DefaultValue: "",
            EntityName: "Local",
            GridDisplayType: "Document",
            Header: "",
            Id: null,
            Index: -1,
            IsFilterable: false,
            IsGroupable: false,
            IsMainEntityColumn: false,
            IsPicklistColumn: false,
            IsReadOnly: true,
            IsReferenceField: false,
            IsSortable: false,
            PicklistGroupId: null,
            PropertyName: "Documents",
            Visibility: GridColumnMode.ViewAndEdit,
            Width: 40
          };
          this.gridDefinition.columns.push(docColumn);
        }
        this.gridDefinition.columns = this.kendoConverter.sortAndRenameColumns(
          this.gridDefinition.columns
        );
        this.dataStateChange();

        this.getSystemPicklistColumns();
      });
  }

  public dataStateChange(state?: DataStateChangeEvent): void {
    this.gridDefinition.isLoading = true;
    this.currentSelected = [];
    if (state) {
      if (state.sort !== this.gridDefinition.state.sort) {
        const filteredSort = state.sort.filter(
          x => x.field !== "Local" + kendoPropertySplitter + "Documents"
        );
        if (state.sort.length !== filteredSort.length) {
          this.gridDefinition.isLoading = false;
          return;
        }
      }
      if (
        state.sort !== this.gridDefinition.state.sort &&
        this.gridDefinition.state.sort &&
        this.gridDefinition.state.sort.length > 0
      ) {
        const newSort: SortDescriptor[] = [];
        for (let index = 0; index < state.sort.length; index++) {
          const element = state.sort[index];
          if (element.dir) {
            newSort.push(element);
          }
        }
        if (newSort.length === this.gridDefinition.state.sort.length) {
          for (
            let index = 0;
            index < this.gridDefinition.state.sort.length;
            index++
          ) {
            const element = this.gridDefinition.state.sort[index];
            this.gridDefinition.state.sort[index].dir = newSort.filter(
              temp => temp.field === element.field
            )[0].dir;
          }
        } else {
          this.gridDefinition.state.sort = newSort;
        }
      } else {
        this.gridDefinition.state = state;
      }
    }
    const query = this.kendoConverter.stateToQuery(this.gridDefinition.state);
    if (this.gridDefinition.project && this.gridDefinition.group) {
      query.Projects = [];
      query.Projects.push(this.gridDefinition.project);
    }
    query.PreFilterGroup = this.gridDefinition.preFilter;

    if (this.gridDefinition.includeHasDocuments) {
      query.IncludeHasDocuments = this.gridDefinition.includeHasDocuments;
    }

    this.gridService
      .postDataGridQuery(this.gridDefinition.gridId, query)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(item => {
        this.gridDefinition.localData = this.kendoConverter.convertDataFromApi(
          item.Entries
        );
        this.gridDefinition.gridData = process(
          this.gridDefinition.localData.data,
          this.dummyState
        );
        this.gridDefinition.gridData.total = item.FilteredCount;
        this.gridDefinition.totalAfterFiltering = item.FilteredCount;
        this.gridDefinition.totalBeforeFiltering = item.TotalCount;
        this.gridDefinition.isLoading = false;
      });
  }

  selectionChangeEvent(changes: any) {
    this.CurrentSelected.next(changes);
  }

  updateFilter(
    filterDescriptor: CompositeFilterDescriptor,
    filterService: FilterService
  ) {
    filterService.filter(filterDescriptor);
  }

  public getPageInfoText(currentPage: number, totalPages: number): string {
    return `Page ${currentPage} of ${totalPages} (${
      this.gridDefinition.totalAfterFiltering
    } of ${this.gridDefinition.totalBeforeFiltering})`;
  }
}
